export const CONFIG = {
  githubUsername: 'npapatheodorou',
  contactEmail: 'n.papatheodorou@pm.me',
  resumeUrl: (process.env.PUBLIC_URL || '') + '/Papatheodorou_Nikos_CV.pdf',
  // Drop a real headshot at public/headshot.jpg (or .png and update the ext) to
  // replace the Gravatar — the UI falls back to Gravatar automatically if the
  // file is missing, so nothing breaks before it's added.
  headshotUrl: (process.env.PUBLIC_URL || '') + '/headshot.jpg',
  gravatarUrl: 'https://www.gravatar.com/avatar/919530f5105d2a1d388bf1ed6dba3e2eb29f1531e27c81dc3950564f5595f792?s=256',
  location: 'Athens, Greece',
  availability: 'Open to DevOps, platform & backend roles',
  headline: 'DevOps Engineer | Java • Spring • Quarkus | Secure, Scalable Systems',
  linkedIn: 'https://www.linkedin.com/in/npapatheodorou',
  name: 'Nikos Papatheodorou',
  title: 'DevOps Engineer',
  subtitle: 'Building Secure, Scalable, and Automated Systems',
};

// Trimmed for recruiter scannability — fewer, higher-signal destinations.
// `overview` and `repositories` remain reachable in-page; they are intentionally
// not nav chips to keep the bar to ~6 items.
// Order mirrors the page: Work → Apps → Experience → Skills → Certifications → Publications → Contact.
export const NAV_ITEMS = [
  { id: 'projects', label: 'Work' },
  { id: 'apps', label: 'Apps' },
  { id: 'career', label: 'Experience' },
  { id: 'skills', label: 'Skills' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'publications', label: 'Publications' },
  { id: 'contact', label: 'Contact' },
];

// Proficiency is expressed through real years of hands-on use, not self-assigned
// percentages (which read as junior to technical reviewers). The UI derives a
// tier — Expert (5y+) / Advanced (3-4y) / Proficient (<3y) — from `years`.
export const SKILLS_DATA = {
  backend: {
    title: 'Backend Development',
    icon: 'server',
    color: 'from-blue-500 to-blue-700',
    skills: [
      { name: 'Java', years: 6, category: 'Language' },
      { name: 'Spring Framework', years: 5, category: 'Framework' },
      { name: 'Spring Boot', years: 5, category: 'Framework' },
      { name: 'Quarkus', years: 3, category: 'Framework' },
      { name: 'REST API Design', years: 6, category: 'Architecture' },
      { name: 'Microservices', years: 4, category: 'Architecture' },
      { name: 'Hibernate/JPA', years: 5, category: 'ORM' },
      { name: 'Maven/Gradle', years: 6, category: 'Build Tools' },
    ],
  },
  devops: {
    title: 'DevOps & Infrastructure',
    icon: 'settings',
    color: 'from-green-500 to-emerald-700',
    skills: [
      { name: 'CI/CD Pipelines', years: 4, category: 'Automation' },
      { name: 'Docker', years: 5, category: 'Containerization' },
      { name: 'Kubernetes', years: 3, category: 'Orchestration' },
      { name: 'Terraform', years: 3, category: 'IaC' },
      { name: 'Ansible', years: 3, category: 'Configuration' },
      { name: 'Jenkins', years: 4, category: 'CI/CD' },
      { name: 'GitHub Actions', years: 3, category: 'CI/CD' },
      { name: 'GitLab CI', years: 3, category: 'CI/CD' },
      { name: 'AWS', years: 3, category: 'Cloud' },
      { name: 'Linux Administration', years: 6, category: 'Systems' },
    ],
  },
  performance: {
    title: 'Performance & Reliability',
    icon: 'zap',
    color: 'from-amber-500 to-orange-600',
    skills: [
      { name: 'Database Optimization', years: 4, category: 'Database' },
      { name: 'PostgreSQL', years: 5, category: 'Database' },
      { name: 'Redis Caching', years: 3, category: 'Caching' },
      { name: 'System Monitoring', years: 4, category: 'Observability' },
      { name: 'Prometheus/Grafana', years: 3, category: 'Monitoring' },
      { name: 'ELK Stack', years: 2, category: 'Logging' },
      { name: 'Load Testing', years: 3, category: 'Testing' },
      { name: 'High Availability Design', years: 3, category: 'Architecture' },
    ],
  },
  security: {
    title: 'Security',
    icon: 'shield',
    color: 'from-red-500 to-rose-700',
    skills: [
      { name: 'Secure Architecture', years: 4, category: 'Design' },
      { name: 'OAuth2/OIDC', years: 3, category: 'Auth' },
      { name: 'JWT/Token Management', years: 4, category: 'Auth' },
      { name: 'Blockchain/SSI', years: 2, category: 'Research' },
      { name: 'SAST/DAST Integration', years: 2, category: 'DevSecOps' },
      { name: 'Secret Management', years: 3, category: 'Security' },
      { name: 'Network Security', years: 3, category: 'Infrastructure' },
    ],
  },
  research: {
    title: 'Research & Teaching',
    icon: 'book',
    color: 'from-purple-500 to-violet-700',
    skills: [
      { name: 'C Programming', years: 4, category: 'Language' },
      { name: 'Python', years: 4, category: 'Language' },
      { name: 'Academic Writing', years: 3, category: 'Research' },
      { name: 'Technical Mentoring', years: 4, category: 'Teaching' },
      { name: 'Workshop Facilitation', years: 3, category: 'Teaching' },
    ],
  },
};

export const PROJECTS_DATA = [
  {
    id: 1,
    title: 'Enterprise CI/CD Pipeline Modernization',
    category: 'DevOps',
    challenge: 'Legacy deployment process with 4-hour release cycles, frequent rollback failures, and zero automated testing in the pipeline.',
    solution: 'Designed and implemented a full GitOps-based CI/CD pipeline using Jenkins, Docker, and Kubernetes with automated quality gates, security scanning, and blue-green deployments.',
    architecture: [
      'Multi-stage Jenkins pipelines with shared libraries',
      'Docker multi-stage builds for optimized images',
      'Kubernetes rolling updates with health checks',
      'Integrated SonarQube, OWASP ZAP, and Trivy scanning',
      'Automated rollback with canary analysis',
    ],
    tools: ['Jenkins', 'Docker', 'Kubernetes', 'SonarQube', 'Helm', 'ArgoCD'],
    outcomes: [
      'Replaced slow, manual releases with automated on-demand deployments',
      'Added quality gates and security scanning (SAST/DAST/image scan) to the pipeline',
      'Introduced safe progressive rollouts with automated rollback on failure',
    ],
    flow: ['Commit', 'Build', 'Test', 'Scan', 'Deploy', 'Rollback'],
    icon: 'rocket',
    gradient: 'from-blue-600 to-cyan-500',
  },
  {
    id: 2,
    title: 'High-Performance Spring Boot Microservices',
    category: 'Backend',
    challenge: 'Monolithic application handling 10K+ concurrent users with 3-second average response times and frequent OOM crashes under peak load.',
    solution: 'Decomposed monolith into Spring Boot microservices with proper domain boundaries, implemented Redis caching, database read replicas, and async processing.',
    architecture: [
      'Domain-Driven Design for service boundaries',
      'Spring Cloud Gateway for API routing',
      'Redis cluster for distributed caching',
      'PostgreSQL with read replicas and connection pooling',
      'RabbitMQ for async event processing',
    ],
    tools: ['Spring Boot', 'Redis', 'PostgreSQL', 'RabbitMQ', 'Docker', 'Kubernetes'],
    outcomes: [
      'Decomposed a monolith into bounded microservices behind an API gateway',
      'Reduced tail latency with distributed caching and database read replicas',
      'Improved horizontal scalability and isolated failures between services',
    ],
    flow: ['Request', 'API Gateway', 'Microservices', 'Redis Cache', 'PostgreSQL'],
    icon: 'zap',
    gradient: 'from-green-600 to-emerald-500',
  },
  {
    id: 3,
    title: 'Infrastructure as Code Automation',
    category: 'Infrastructure',
    challenge: 'Manual server provisioning taking 2-3 days per environment with configuration drift causing production incidents monthly.',
    solution: 'Implemented complete IaC using Terraform for cloud provisioning and Ansible for configuration management, with environment parity enforcement.',
    architecture: [
      'Terraform modules for reusable infrastructure components',
      'Ansible roles for application configuration',
      'GitOps workflow for infrastructure changes',
      'Automated compliance scanning with InSpec',
      'Environment promotion pipeline (dev → staging → prod)',
    ],
    tools: ['Terraform', 'Ansible', 'AWS', 'GitLab CI', 'InSpec', 'Vault'],
    outcomes: [
      'Codified environments with reusable Terraform modules and Ansible roles',
      'Eliminated configuration drift through GitOps-driven infrastructure changes',
      'Made dev/staging/prod reproducible and promotable through a pipeline',
    ],
    flow: ['Git', 'Terraform', 'Ansible', 'Provision', 'Compliance', 'Promote'],
    icon: 'layers',
    gradient: 'from-purple-600 to-violet-500',
  },
  {
    id: 4,
    title: 'Secure Authentication & Identity Platform',
    category: 'Security',
    challenge: 'Fragmented authentication across 12 services with inconsistent security policies and no centralized audit logging.',
    solution: 'Built centralized identity platform with OAuth2/OIDC, incorporating SSI research concepts for user-controlled data, with complete audit trail.',
    architecture: [
      'Keycloak-based identity provider with custom extensions',
      'OAuth2 Authorization Code flow with PKCE',
      'JWT token management with rotation policies',
      'Centralized audit logging with tamper detection',
      'GDPR-compliant consent management',
    ],
    tools: ['Keycloak', 'Spring Security', 'JWT', 'PostgreSQL', 'ELK Stack', 'Vault'],
    outcomes: [
      'Centralized authentication across services on OAuth2/OIDC with SSO',
      'Standardized token policies and added centralized, tamper-evident audit logging',
      'Applied SSI research concepts for user-controlled, privacy-preserving identity',
    ],
    flow: ['Login', 'Keycloak', 'OAuth2 / OIDC', 'JWT', 'Audit Log'],
    icon: 'shield',
    gradient: 'from-red-600 to-rose-500',
  },
  {
    id: 5,
    title: 'Observability & Monitoring Platform',
    category: 'Reliability',
    challenge: 'Zero visibility into system health, MTTR of 4+ hours for incidents, and no proactive alerting.',
    solution: 'Implemented comprehensive observability stack with metrics, logging, and distributed tracing, with intelligent alerting.',
    architecture: [
      'Prometheus for metrics collection with custom exporters',
      'Grafana dashboards with SLO/SLI tracking',
      'ELK Stack for centralized logging',
      'Jaeger for distributed tracing',
      'PagerDuty integration with escalation policies',
    ],
    tools: ['Prometheus', 'Grafana', 'ELK Stack', 'Jaeger', 'PagerDuty', 'AlertManager'],
    outcomes: [
      'Unified metrics, logs, and distributed traces into one observability stack',
      'Added SLO/SLI dashboards and actionable, low-noise alerting',
      'Reduced time-to-detection and mean-time-to-resolution for incidents',
    ],
    flow: ['Metrics', 'Logs', 'Traces', 'Dashboards', 'Alerts'],
    icon: 'activity',
    gradient: 'from-amber-600 to-yellow-500',
  },
  {
    id: 6,
    title: 'Quarkus Cloud-Native Migration',
    category: 'Backend',
    challenge: 'Spring Boot services with 30-second startup times and 512MB+ memory footprint, causing slow auto-scaling.',
    solution: 'Migrated critical path services to Quarkus with GraalVM native compilation, achieving sub-second startup.',
    architecture: [
      'Quarkus with GraalVM native-image compilation',
      'RESTEasy Reactive for non-blocking I/O',
      'Hibernate Reactive with Vert.x SQL client',
      'SmallRye Config for 12-factor app configuration',
      'Kubernetes-native health checks and scaling',
    ],
    tools: ['Quarkus', 'GraalVM', 'Kubernetes', 'Vert.x', 'Docker', 'Helm'],
    outcomes: [
      'Migrated latency-critical services to Quarkus with GraalVM native images',
      'Achieved sub-second startup and a substantially smaller memory footprint',
      'Improved autoscaling responsiveness and reduced cloud runtime cost',
    ],
    flow: ['Source', 'GraalVM Build', 'Native Image', 'Deploy', 'Autoscale'],
    icon: 'cpu',
    gradient: 'from-teal-600 to-cyan-500',
  },
];

// Shipped, publicly usable apps. Facts are taken from each app's live site and
// README. `noBackend` marks apps that run entirely client-side (no accounts,
// no server-side storage); `featured` gets the wide bento slot.
export const APPS_DATA = [
  {
    id: 'notesilo',
    name: 'NoteSilo',
    tagline: 'A note editor that never phones home.',
    category: 'Productivity',
    description: 'Local-first Markdown editor that runs entirely in the browser and saves to a .json file you own. Notes persist in IndexedDB and sync to disk — no backend, no cloud sync, no analytics.',
    highlights: [
      'Monaco-powered editing: multi-cursor, column selection, syntax-highlighted code blocks',
      'Drag-and-drop note tree with groups, custom icons and colours',
      'Full-text and regex search-and-replace across every document',
    ],
    stack: ['React 19', 'TypeScript', 'Vite', 'Zustand', 'Monaco Editor', 'IndexedDB'],
    liveUrl: 'https://notesilo.vercel.app/',
    repoUrl: 'https://github.com/npapatheodorou/notesilo',
    icon: 'notes',
    iconSrc: '/icons/apps/notesilo.svg',
    gradient: 'from-primary-600 to-accent-500',
    noBackend: true,
  },
  {
    id: 'xbs-pwa',
    name: 'xBrowserSync Bookmarks',
    tagline: 'Your xBrowserSync bookmarks, on your homescreen.',
    category: 'PWA · Security',
    description: 'Unofficial read-only PWA client for xBrowserSync — the iOS/Android homescreen app it never had. Bookmarks are decrypted on-device, so your password never leaves the browser.',
    highlights: [
      'On-device decryption with the Web Crypto API (PBKDF2-SHA256, AES-GCM)',
      'Installable PWA with offline access to cached bookmarks',
      'Search across titles, URLs and tags; collapsible folder tree',
    ],
    stack: ['Vanilla JS', 'Web Crypto API', 'Service Worker', 'PWA', 'Zero dependencies'],
    liveUrl: 'https://xbs-pwa.vercel.app/',
    repoUrl: 'https://github.com/npapatheodorou/xbs-pwa',
    icon: 'bookmark',
    iconSrc: '/icons/apps/xbs-pwa.png',
    gradient: 'from-purple-600 to-violet-500',
    noBackend: true,
  },
  {
    id: 'nordconf-wireguard',
    name: 'NordConf WireGuard',
    tagline: 'NordVPN\u2019s best server, as a ready-to-import .conf.',
    category: 'Networking',
    description: 'Turns NordVPN\u2019s recommended NordLynx server into a downloadable WireGuard configuration. The private key is fetched with your access token and used in the browser only.',
    highlights: [
      'Auto-picks the lowest-load recommended server, or choose one manually on a map',
      'Private key is generated and used client-side — never uploaded',
      'Self-hostable; a single serverless function proxies the NordVPN API',
    ],
    stack: ['JavaScript', 'Vercel Serverless', 'WireGuard', 'NordVPN API'],
    liveUrl: 'https://nordconf-wireguard.vercel.app/',
    repoUrl: 'https://github.com/npapatheodorou/nordconf-wireguard',
    icon: 'key',
    iconSrc: '/icons/apps/nordconf-wireguard.svg',
    // Shield-on-transparent favicon; brand navy behind it so it reads in light mode.
    iconBg: '#0d1421',
    iconPad: true,
    gradient: 'from-blue-600 to-cyan-500',
    noBackend: true,
  },
  {
    id: 'loadoutify',
    name: 'Loadoutify',
    tagline: 'Set up Windows in one pass.',
    category: 'Windows tooling',
    description: 'Pick your tools from a curated catalog and get one elevated Chocolatey script for everything it can install, plus a checklist of vendor downloads for the rest.',
    highlights: [
      '122 curated tools across 13 categories',
      'One unattended Chocolatey run with per-package status tracking',
      'Share a complete loadout by URL — no accounts, no database',
    ],
    stack: ['Chocolatey', 'PowerShell', 'Windows'],
    liveUrl: 'https://loadoutify.vercel.app/',
    repoUrl: null,
    icon: 'package',
    iconSrc: '/icons/apps/loadoutify.svg',
    gradient: 'from-amber-600 to-orange-500',
    noBackend: true,
  },
  {
    id: 'cudy-openwrt-tutorial',
    name: 'Cudy OpenWrt Guide',
    tagline: 'Flash OpenWrt on a Cudy router and unlock it.',
    category: 'Networking · Docs',
    description: 'Interactive step-by-step guide for 18 Cudy router variants: flashing OpenWrt, enabling LuCI, network-wide Adblock, WireGuard VPN with policy routing and a kill switch, and IPv6.',
    highlights: [
      'Covers 18 Cudy models, with the WR3000E recommended',
      'Adblock, WireGuard VPN, policy-based routing and kill switch',
      'Command snippets stay in sync between README and site via embedded markers',
    ],
    stack: ['React', 'TypeScript', 'Vite', 'OpenWrt', 'WireGuard'],
    liveUrl: 'https://cudy-openwrt-tutorial.vercel.app/',
    repoUrl: 'https://github.com/npapatheodorou/cudy-openwrt-tutorial',
    icon: 'wifi',
    iconSrc: '/icons/apps/cudy-openwrt-tutorial.svg',
    gradient: 'from-green-600 to-emerald-500',
    ctaLabel: 'Read guide',
    noBackend: false,
  },
  {
    id: 'pretty-examtopics-downloader',
    name: 'ExamTopics Downloader',
    tagline: 'Public ExamTopics questions, as an offline study file.',
    category: 'CLI · Go',
    type: 'tool',
    description: 'Enhanced edition of the community ExamTopics downloader. Pulls publicly accessible question pages for a chosen exam and renders them into one clean, self-contained HTML study file \u2014 no paywall bypass, no login.',
    highlights: [
      'Interactive vendor/exam picker with rate-limited, cached and resumable discovery',
      'Single HTML output with styled question cards and an exam-simulation quiz mode',
      'Pre-built Windows executables; builds for macOS and Linux from source',
    ],
    stack: ['Go', 'CLI', 'HTML'],
    liveUrl: 'https://github.com/npapatheodorou/pretty-examtopics-downloader',
    repoUrl: 'https://github.com/npapatheodorou/pretty-examtopics-downloader',
    icon: 'terminal',
    gradient: 'from-rose-600 to-red-500',
    ctaLabel: 'View on GitHub',
    noBackend: true,
  },
];

// Repo names to pin at the top of the Repositories section's Featured
// Projects, in APPS_DATA order. Derived from repoUrl so both sections stay in
// sync automatically; also lets a substantially reworked fork through the
// "no forks" filter (GitHub flags the examtopics downloader as a fork).
// Housekeeping repos that add noise rather than signal to the public list.
export const HIDDEN_REPOS = ['npapatheodorou', 'npapatheodorou.github.io'];

export const FEATURED_REPO_NAMES = APPS_DATA
  .filter(function (app) { return app.repoUrl; })
  .map(function (app) { return app.repoUrl.split('/').pop(); });

export const CAREER_DATA = [
  {
    id: 1,
    title: 'DevOps Engineer',
    company: 'Ernst & Young (EY)',
    employmentType: 'Full-time',
    period: 'Jul 2024 - Present',
    duration: '2 yrs 1 mo',
    location: 'Athens, Attiki, Greece - Hybrid',
    type: 'current',
    logo: 'ey',
    // Single company, two consultant grades — rendered as a nested promotion
    // path inside the EY card (newest first) to show growth in one place.
    roles: [
      {
        title: 'Senior Consultant II – DevOps Engineer',
        period: 'Oct 2025 - Present',
        duration: '9 mos',
        current: true,
        promoted: true,
      },
      {
        title: 'Senior Consultant I – DevOps Engineer',
        period: 'Jul 2024 - Oct 2025',
        duration: '1 yr 4 mos',
        current: false,
        promoted: false,
      },
    ],
    summary: 'Building and maintaining CI/CD workflows, deployment automation, and cloud-native delivery pipelines for enterprise services. Promoted from Senior Consultant I to Senior Consultant II.',
    highlights: [
      'Designed and improved CI/CD pipelines for faster and safer releases',
      'Automated environment provisioning and deployment workflows',
      'Supported reliable operations across development and production environments',
    ],
    technologies: ['Azure', 'CI/CD', 'Docker', 'Kubernetes', 'Terraform', 'Java', 'Spring Boot'],
  },
  {
    id: 2,
    title: 'Back-end Software Engineer',
    company: 'APT Information Systems',
    employmentType: 'Full-time',
    period: 'Jan 2021 - Jun 2024',
    duration: '3 yrs 6 mos',
    location: 'Athens, Attiki, Greece',
    type: 'past',
    logo: 'apt',
    summary: 'Developed and maintained backend systems with Spring Framework and Java for production business applications.',
    highlights: [
      'Built backend APIs and business services using Java and Spring technologies',
      'Collaborated on architecture, implementation, and maintenance of core systems',
      'Delivered stable features and improvements across multiple product releases',
    ],
    technologies: ['Java', 'Spring Framework', 'Spring Boot', 'REST APIs', 'SQL', 'Hibernate/JPA'],
  },
  {
    id: 3,
    title: 'Academic Tutor',
    company: 'Hellenic Mediterranean University',
    employmentType: 'Academic',
    period: 'Oct 2019 - Feb 2021',
    duration: '1 yr 5 mos',
    location: 'Heraklion, Crete, Greece',
    type: 'academic',
    logo: 'hmu',
    summary: 'Consolidated concurrent tutoring roles into one academic position, supporting undergraduate teaching across core informatics modules.',
    highlights: [
      'Delivered labs, tutorials, and student support across multiple courses',
      'Prepared educational material and guided students through practical exercises',
    ],
    courses: [
      'Information Systems Security (TP-122)',
      'Algorithms (TP-313)',
      'Databases (TP-132)',
      'Object Oriented Programming (ECE-136)',
      'Scientific Programming with Python (ECE-102)',
      'Programming Languages (TP-338)',
      'Modern Topics in Informatics (TP-105)',
    ],
    technologies: ['Teaching', 'Java', 'Python', 'Databases', 'Algorithms', 'Programming Languages'],
  },
];

export const RESEARCH_DATA = {
  education: [
    {
      degree: 'MSc — Hellenic Mediterranean University',
      field: 'Informatics Engineering',
      period: '2019 – 2021',
      detail: 'Thesis on Blockchain-based Self-Sovereign Identity (SSI) — graded 10/10.',
    },
    {
      degree: 'BSc — Hellenic Mediterranean University',
      field: 'Informatics Engineering',
      period: '2014 – 2019',
      detail: 'Foundations in software engineering, systems, databases, and security.',
    },
  ],
  thesis: {
    title: 'Blockchain-based Self-Sovereign Identity (SSI)',
    degree: 'Master of Science',
    description: 'Researched and implemented a decentralized identity management system leveraging blockchain technology to give users full control over their digital identities, eliminating reliance on centralized identity providers.',
    contributions: [
      'Designed a novel SSI architecture using distributed ledger technology',
      'Implemented proof-of-concept with verifiable credentials',
      'Analyzed security properties including privacy, integrity, and availability',
      'Evaluated performance characteristics against traditional identity systems',
      'Published findings with recommendations for enterprise adoption',
    ],
    keywords: ['Blockchain', 'Self-Sovereign Identity', 'Decentralized Identity', 'Verifiable Credentials', 'DID', 'Privacy'],
  },
  presentations: [
    {
      title: 'Linaro Connect Workshop',
      venue: 'Linaro Connect Conference',
      type: 'Workshop',
      description: 'Led a hands-on workshop on embedded systems development and Linux-based toolchains for IoT devices.',
    },
    {
      title: 'Blockchain Identity Research',
      venue: 'Academic Conference',
      type: 'Presentation',
      description: 'Presented MSc thesis findings on blockchain-based identity systems and their applicability to enterprise environments.',
    },
  ],
  teaching: [
    {
      course: 'Java Programming',
      level: 'Undergraduate',
      description: 'Taught object-oriented programming principles, data structures, and software design patterns using Java.',
    },
    {
      course: 'C Programming',
      level: 'Undergraduate',
      description: 'Instructed systems programming, memory management, and low-level programming concepts in C.',
    },
    {
      course: 'Python Programming',
      level: 'Undergraduate',
      description: 'Taught scripting, data manipulation, and introductory algorithms using Python.',
    },
  ],
};

export const PUBLICATIONS_DATA = [
  {
    id: 1,
    title: 'The YouGovern Secure Blockchain-Based Self-Sovereign Identity (SSI) Management and Access Control',
    authors: 'Nikolaos Papatheodorou et al.',
    venue: 'MDPI Applied Sciences',
    venueShort: 'Applied Sciences',
    volume: '15(12), 6437',
    publisher: 'MDPI',
    date: 'June 7, 2025',
    year: 2025,
    doi: null,
    url: 'https://www.mdpi.com/2076-3417/15/12/6437',
    type: 'Journal Article',
    tags: ['Blockchain', 'Self-Sovereign Identity', 'Access Control', 'Security', 'Decentralized Identity'],
    abstract: 'Presents a secure blockchain-based Self-Sovereign Identity (SSI) management system with robust access control mechanisms, enabling users to maintain full ownership of their digital identities without relying on centralized authorities.',
    color: 'from-purple-600 to-violet-500',
    icon: 'journal',
  },
  {
    id: 2,
    title: 'Secure Authentication in the Presence of Malicious Messages and Packet Reorders: Study on CAN Bus',
    authors: 'Nikolaos Papatheodorou et al.',
    venue: '2025 IEEE 15th Annual Computing and Communication Workshop and Conference (CCWC)',
    venueShort: 'IEEE CCWC 2025',
    volume: null,
    publisher: 'IEEE',
    date: 'March 5, 2025',
    year: 2025,
    doi: '10.1109/CCWC62904.2025.10903953',
    url: 'https://ieeexplore.ieee.org/document/10903953',
    type: 'Conference Paper',
    tags: ['CAN Bus', 'Automotive Security', 'Authentication', 'Embedded Systems', 'Network Security'],
    abstract: 'Investigates secure authentication protocols for Controller Area Network (CAN) bus systems under adversarial conditions, addressing malicious message injection and packet reordering attacks in vehicular networks.',
    color: 'from-blue-600 to-cyan-500',
    icon: 'conference',
  },
  {
    id: 3,
    title: 'DoS Detection on In-Vehicle Networks: Evaluation on an Experimental Embedded System Platform',
    authors: 'Nikolaos Papatheodorou et al.',
    venue: 'Applications in Electronics Pervading Industry, Environment and Society (ApplePies 2021)',
    venueShort: 'ApplePies 2021',
    volume: 'Lecture Notes in Electrical Engineering, vol 866',
    publisher: 'Springer',
    date: 'April 9, 2022',
    year: 2022,
    doi: '10.1007/978-3-030-95498-7_37',
    url: 'https://link.springer.com/chapter/10.1007/978-3-030-95498-7_37',
    type: 'Book Chapter',
    tags: ['DoS Detection', 'In-Vehicle Networks', 'Embedded Systems', 'Intrusion Detection', 'CAN Bus'],
    abstract: 'Evaluates Denial-of-Service detection mechanisms for in-vehicle networks using an experimental embedded system platform, demonstrating practical approaches to securing automotive communication systems.',
    color: 'from-green-600 to-emerald-500',
    icon: 'book',
  },
  {
    id: 4,
    title: 'A Probabilistic Logic Program that Predicts COVID-19 Infection and the Possibility a Patient to Recover',
    authors: 'Nikolaos Papatheodorou et al.',
    venue: '24th Pan-Hellenic Conference on Informatics (PCI 2020)',
    venueShort: 'PCI 2020',
    volume: null,
    publisher: 'ACM',
    date: 'November 20, 2020',
    year: 2020,
    doi: '10.1145/3437120.3437323',
    url: 'https://dl.acm.org/doi/10.1145/3437120.3437323',
    type: 'Conference Paper',
    tags: ['Probabilistic Logic', 'COVID-19', 'Machine Learning', 'Healthcare', 'Prediction'],
    abstract: 'Develops a probabilistic logic programming approach for predicting COVID-19 infection likelihood and patient recovery probability, combining logical reasoning with probabilistic inference for healthcare decision support.',
    color: 'from-amber-600 to-orange-500',
    icon: 'conference',
  },
];

export const CERTIFICATIONS_DATA = [
  {
    id: 1,
    title: 'Microsoft Certified: DevOps Engineer Expert',
    issuer: 'Microsoft',
    issued: 'June 2026',
    expires: 'June 2027',
    credentialId: '60626A10BBAAAAF1',
    url: 'https://learn.microsoft.com/api/credentials/share/en-us/NikosPapatheodorou-7173/6443C7754EBA709A?sharingId=FA70112CAD0908A6',
    logo: 'microsoft',
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-600',
    tags: ['DevOps', 'Azure', 'CI/CD', 'IaC', 'Automation'],
    description: 'Validates expertise in designing and implementing DevOps practices on Azure — CI/CD pipelines, infrastructure as code, configuration management, security, compliance, and continuous monitoring across the full development lifecycle.',
    level: 'Expert',
    status: 'active',
  },
  {
    id: 2,
    title: 'Certified Vaadin 24 Developer',
    issuer: 'Vaadin',
    issued: 'February 2026',
    expires: null,
    credentialId: '#5a033b87-83e3-491e-b28f-ba14a3a3dc79',
    url: 'https://vaadin.com/learn/certificate/5a033b87-83e3-491e-b28f-ba14a3a3dc79',
    logo: 'vaadin',
    color: 'from-cyan-500 to-blue-600',
    bgColor: 'bg-cyan-600',
    tags: ['Java', 'Web Framework', 'Full-Stack', 'UI Development'],
    description: 'Certifies proficiency in building modern web applications using the Vaadin 24 framework with Java-based full-stack development capabilities.',
    level: 'Professional',
    status: 'active',
  },
  {
    id: 3,
    title: 'Microsoft Certified: Azure Administrator Associate',
    issuer: 'Microsoft',
    issued: 'February 2026',
    expires: 'February 2027',
    credentialId: '60626A10BBAAAAF1',
    url: 'https://learn.microsoft.com/en-us/users/nikospapatheodorou-7173/credentials/60626a10bbaaaaf1',
    logo: 'microsoft',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500',
    tags: ['Azure', 'Cloud', 'Infrastructure', 'Administration'],
    description: 'Validates expertise in implementing, managing, and monitoring Azure environments including virtual networks, storage, compute, identity, security, and governance.',
    level: 'Associate',
    status: 'active',
  },
  {
    id: 4,
    title: 'AWS Certified Cloud Practitioner',
    issuer: 'Amazon Web Services (AWS)',
    issued: 'February 2026',
    expires: 'February 2029',
    credentialId: '0596115d166442fc96f08325de4f2992',
    url: 'https://www.credly.com/badges/a55933c0-a47d-47e1-a082-5fbc9bb545a8',
    logo: 'aws',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500',
    tags: ['AWS', 'Cloud', 'Architecture', 'Services'],
    description: 'Demonstrates foundational understanding of AWS Cloud concepts, services, security, architecture, pricing, and support to build cloud fluency.',
    level: 'Foundational',
    status: 'active',
  },
  {
    id: 5,
    title: 'Oracle Certified Professional: Java SE 17 Developer',
    issuer: 'Oracle',
    issued: 'October 2023',
    expires: null,
    credentialId: '100072762OCPJSE17',
    url: 'https://catalog-education.oracle.com/ords/certview/sharebadge?id=3B1662C516A0423B37904FF9E7349892FF420C6F54F49008143EE2D814F6D72E',
    logo: 'oracle',
    color: 'from-red-500 to-red-700',
    bgColor: 'bg-red-600',
    tags: ['Java', 'SE 17', 'OOP', 'Core Java'],
    description: 'Validates advanced Java programming skills including modules, concurrency, I/O, annotations, generics, and the Java platform module system in Java SE 17.',
    level: 'Professional',
    status: 'active',
  },
  {
    id: 6,
    title: 'Certified Vaadin 14 Developer',
    issuer: 'Vaadin',
    issued: 'March 2021',
    expires: null,
    credentialId: '#9e156ab3-a996-4365-9694-871c6ee6016d',
    url: 'https://vaadin.com/learn/certificate/9e156ab3-a996-4365-9694-871c6ee6016d',
    logo: 'vaadin',
    color: 'from-teal-500 to-cyan-600',
    bgColor: 'bg-teal-600',
    tags: ['Java', 'Web Framework', 'Full-Stack', 'UI Development'],
    description: 'Certifies proficiency in building web applications using the Vaadin 14 framework with Java-based component architecture.',
    level: 'Professional',
    status: 'active',
  },
];

export const LANGUAGE_COLORS = {
  Java: '#b07219',
  JavaScript: '#f1e05a',
  Python: '#3572A5',
  C: '#555555',
  'C++': '#f34b7d',
  Go: '#00ADD8',
  TypeScript: '#3178c6',
  Shell: '#89e051',
  Dockerfile: '#384d54',
  HCL: '#844FBA',
  Kotlin: '#A97BFF',
  Groovy: '#4298b8',
  HTML: '#e34c26',
  CSS: '#563d7c',
  YAML: '#cb171e',
  Makefile: '#427819',
};

// Verifiable, non-inflated facts only — each is backed by another section on the
// page (Career, Publications, Certifications), so a recruiter can confirm them.
export const STATS_OVERVIEW = [
  { label: 'Years Experience', value: '6+', icon: 'calendar', color: 'text-blue-400' },
  { label: 'Peer-Reviewed Papers', value: '4', icon: 'book', color: 'text-purple-400' },
  { label: 'Certifications', value: '6', icon: 'shield', color: 'text-emerald-400' },
  { label: 'Engineering Roles', value: '3', icon: 'rocket', color: 'text-amber-400' },
];
